import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import TechnologyIcon from 'shared/components/technology/TechnologyIcon';
import { isEarthRangerTypeValue } from 'connections/earthRangerUrl';
import { RAIL_IN_X, RAIL_OUT_X } from './useFlowGraph';
import { Icon } from 'react-icons-kit';
import { ic_check } from 'react-icons-kit/md/ic_check';
import { ic_priority_high } from 'react-icons-kit/md/ic_priority_high';

// The card's height is fixed and mirrored by NODE_H, which React Flow is given as
// the node's declared height: the handles below sit at 35%/65% of it, so a card
// with a URL line and one without still connect at the same heights, and the map
// never has to wait for a measurement to show a node.
const AccountNode = ({ data }: any) => {
  const { t } = useTranslation();
  return (
    <HoverTooltip
      content={
        data.needsSetup
          ? t('Configure Integration — setup needed')
          : t('Configure Integration')
      }
      position="top"
      portal
      focusable={false}
      className="block"
    >
      {/* Two staggered waves behind the box while no configuration is open,
          so every account reads as something to click. */}
      {data.clickHint && (
        <span
          data-testid="AccountNode-click-hint"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 motion-reduce:hidden"
        >
          <span className="absolute inset-0 animate-account-wave rounded-lg bg-greyscale-7" />
          <span className="absolute inset-0 animate-account-wave rounded-lg bg-greyscale-7 [animation-delay:1.2s]" />
        </span>
      )}
      <div
        data-testid={`AccountNode-${data.isProvider ? 'provider' : 'destination'}-div`}
        className={`relative h-[116px] ${data.vertical ? 'w-[260px]' : 'w-[320px]'} cursor-pointer rounded-lg border bg-white px-4 py-3 transition-shadow hover:shadow-md ${
          data.isSelected
            ? 'border-green-1 ring-2 ring-green-1'
            : 'border-ui-divider-lines'
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Logo where there is one, first letter where there isn't — same
                    box either way, so every card keeps the same footprint. */}
          <TechnologyIcon
            typeValue={data.techValue}
            typeName={data.techName}
            boxClassName="w-9 h-9 text-body-md"
            imgClassName="w-9 h-9 object-contain"
          />
          <span
            className="truncate text-body-md font-bold text-ui-text-primary-text"
            title={data.name}
          >
            {data.name}
          </span>
          {/* checklist state: every required field saved (✓) or setup pending (!) */}
          <span
            className={`ml-auto flex h-4 w-4 flex-none items-center justify-center rounded-full ${
              data.needsSetup
                ? 'border border-ui-status-error-destructive bg-ui-status-bg-error-bg text-ui-status-error-destructive'
                : 'bg-teal-t10 text-green-1'
            }`}
            aria-label={
              data.needsSetup
                ? t('Needs setup — required fields are missing')
                : t('Configured')
            }
          >
            <Icon
              icon={data.needsSetup ? ic_priority_high : ic_check}
              size={12}
              // The icon's wrapper takes the text line height (24px) and sits
              // the glyph below the middle of the 16px disc; flex centres it.
              style={{ display: 'flex' }}
            />
          </span>
        </div>
        {/* The address only says something for EarthRanger, where it names the site;
                for every other technology it is an internal endpoint. */}
        {data.base_url && isEarthRangerTypeValue(data.techValue) && (
          <div
            className="mt-0.5 truncate text-body-sm text-ui-text-primary-text"
            title={data.base_url}
          >
            {data.base_url}
          </div>
        )}
        {data.techName && (
          <div className="mt-0.5">
            {/* Truncates rather than wraps — the card height stays fixed. */}
            <span
              className="block truncate text-body-sm text-ui-text-primary-text"
              title={data.techName}
            >
              {data.techName}
            </span>
          </div>
        )}

        {data.isProvider ? (
          <>
            {data.vertical ? (
              <>
                <Handle
                  type="source"
                  id="out-source"
                  position={Position.Bottom}
                  isConnectable={false}
                  className="opacity-0"
                  style={{ left: RAIL_OUT_X }}
                />
                <Handle
                  type="target"
                  id="in-target"
                  position={Position.Bottom}
                  isConnectable={false}
                  className="opacity-0"
                  style={{ left: RAIL_IN_X }}
                />
              </>
            ) : (
              <>
                <Handle
                  type="source"
                  id="out-source"
                  position={Position.Right}
                  isConnectable={false}
                  className="opacity-0"
                  style={{ top: '35%' }}
                />
                <Handle
                  type="target"
                  id="in-target"
                  position={Position.Right}
                  isConnectable={false}
                  className="opacity-0"
                  style={{ top: '65%' }}
                />
              </>
            )}
          </>
        ) : (
          <>
            <Handle
              type="target"
              id="out-target"
              position={Position.Left}
              isConnectable={false}
              className="opacity-0"
              style={{ top: '35%' }}
            />
            <Handle
              type="source"
              id="in-source"
              position={Position.Left}
              isConnectable={false}
              className="opacity-0"
              style={{ top: '65%' }}
            />
          </>
        )}
      </div>
    </HoverTooltip>
  );
};

export default AccountNode;
